import {
    ScorableMetaData,
    ScorableQuestionGroupList,
    ScorableQuestionGroupListSurveys,
} from "./Scorable";
import { selectForm } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    saveScorableSurvey,
    selectHasScorableLoaded,
    selectIsSurveyIncomplete,
    selectIsSurveyExpired,
    submitScorableSurvey,
} from "./scorableSlice";
import { ScoreOverview } from "features/scorable/ScoreOverview";
import "./Survey.less";

import { Markdown } from "features/common/Markdown";
import Footer from "features/qualityForm/formViewer/footer/Footer";
import { FormLocaleProvider } from "features/i18n/useLocale";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { SubmitActionOperations } from "features/qualityForm/formViewer/footer/SubmitActionOperations";
import { getSurveyCodeUrl } from "routes/SurveyCode";
import { useParams } from "react-router";
import { useState, useCallback, useEffect, useRef } from "react";
import scrollToFirstError from "features/qualityForm/common/errorHandler";
import { useStrings } from "features/i18n/useStrings";
import strings from "./Survey.strings";
import { ScorableValidationContext } from "features/qualityForm/common/Contexts";
import { ErrorStatement } from "features/qualityForm/formEditor/common/ErrorStatement/ErrorStatement";
import { Header } from "./Header";
import { ErrorCodes } from "enums/ErrorCodes";
export function SurveyRenderedHeader() {
    const header = useAppSelector((state) => selectForm(state)?.header ?? "");
    return <Markdown markdownText={header}></Markdown>;
}

export function SurveyRenderedFooter() {
    const footer = useAppSelector((state) => selectForm(state)?.footer ?? "");
    return <Markdown markdownText={footer}></Markdown>;
}

export function SurveyPublished() {
    return (
        <div className="Survey">
            <Header />
            <ScorableMetaData />
            <FormLocaleProvider>
                <SurveyRenderedHeader />
                <ScorableQuestionGroupList />
                <Footer>
                    <SurveyRenderedFooter />
                </Footer>
            </FormLocaleProvider>
            <ScoreOverview />
        </div>
    );
}

enum scorableStateValues {
    ALREADY_FINISHED = "already_finished",
    DEFAULT_ERROR = "default_error",
    SUCCESS = "success",
    SUBMIT = "submit",
    SAVE = "save",
}

export function SurveyUnpublished() {
    const t = useStrings(strings);
    const isStatusIncomplete = useAppSelector(selectIsSurveyIncomplete);
    const hasScorableLoaded = useAppSelector(selectHasScorableLoaded);
    const isExpired = useAppSelector(selectIsSurveyExpired);
    const ref = useRef<HTMLDivElement>(null);
    const [scorableState, setScorableState] = useState({
        action: "",
        result: "",
    });
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const { code } = useParams();
    const dispatch = useAppDispatch();
    const onSubmit = useCallback(() => {
        dispatch(submitScorableSurvey(getSurveyCodeUrl(code))).then((e) => {
            if ("error" in e) {
                const payload: any = e.payload ?? {};
                setShowValidationErrors(true);

                if (payload.message === ErrorCodes.SURVEY_IS_ALREADY_FINISHED) {
                    setScorableState({
                        action: scorableStateValues.SUBMIT,
                        result: scorableStateValues.ALREADY_FINISHED,
                    });
                } else {
                    setScorableState({
                        action: scorableStateValues.SUBMIT,
                        result: scorableStateValues.DEFAULT_ERROR,
                    });
                }

                scrollToFirstError(ref);
            } else {
                setScorableState({
                    action: scorableStateValues.SUBMIT,
                    result: scorableStateValues.SUCCESS,
                });
            }
        });
    }, [dispatch, code]);
    const onSave = useCallback(() => {
        dispatch(saveScorableSurvey(getSurveyCodeUrl(code))).then((e) => {
            if ("error" in e) {
                const payload: any = e.payload ?? {};
                if (payload.message === ErrorCodes.SURVEY_IS_ALREADY_FINISHED) {
                    setScorableState({
                        action: scorableStateValues.SAVE,
                        result: scorableStateValues.ALREADY_FINISHED,
                    });
                } else {
                    setScorableState({
                        action: scorableStateValues.SAVE,
                        result: scorableStateValues.DEFAULT_ERROR,
                    });
                }

                scrollToFirstError(ref);
            } else {
                setScorableState({
                    action: scorableStateValues.SAVE,
                    result: scorableStateValues.SUCCESS,
                });
            }
        });
    }, [dispatch, code]);

    useEffect(() => {
        if (scorableState.result === scorableStateValues.DEFAULT_ERROR) {
            scrollToFirstError(ref);
        }
    }, [scorableState]);

    const statusMessage =
        scorableState.result === scorableStateValues.DEFAULT_ERROR ? (
            <ErrorStatement
                errorMessage={
                    scorableState.action === scorableStateValues.SAVE
                        ? t.labels.errors["saveFail.default.message"]
                        : t.labels.errors["submitFail.default.message"]
                }
            />
        ) : scorableState.result === scorableStateValues.ALREADY_FINISHED ? (
            <ErrorStatement
                errorMessage={t.labels.errors["alreadyFinished.message"]}
            />
        ) : scorableState.action === scorableStateValues.SAVE ? (
            <>{t.labels.errors["saveSuccess.message"]}</>
        ) : (
            <></>
        );
    return (
        <div className="Survey in-progress">
            {isExpired ? (
                <h1>{t.labels.expired}</h1>
            ) : (
                <>
                    {isStatusIncomplete ? (
                        <>
                            <SurveyRenderedHeader />
                            <ScorableValidationContext.Provider
                                value={{ showValidationErrors }}
                            >
                                <ScorableQuestionGroupListSurveys />
                            </ScorableValidationContext.Provider>
                            <Footer>
                                <SurveyRenderedFooter />
                            </Footer>
                            <SubmitActionOperations
                                isCompleted={false}
                                rescoreMode={false}
                                toggleRescoreMode={() => {}}
                                isAdmin={false}
                                attemptSubmit={onSubmit}
                                attemptSave={onSave}
                                attemptSaveByAgent={() => {}}
                                calibrationMode={false}
                                calibrationError={() => {}}
                                setSideDrawerMode={() => {}}
                            />
                            {statusMessage}
                        </>
                    ) : (
                        <>
                            {hasScorableLoaded ? (
                                <>
                                    <h1>{t.labels.completed.thankyou}</h1>
                                    <div>{t.labels.completed.poweredBy}</div>
                                </>
                            ) : (
                                <></>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
